import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth/useAuth";
import { useRole } from "../lib/auth/useRole";
import { useWorkerTimelineDays } from "../hooks/useWorkerTimelineDays";
import { useWorkerTimeDays } from "../hooks/useWorkerTimeDays";
import { useAutoAttach } from "../hooks/useAutoAttach";
import { useUnassignedTime } from "../hooks/useUnassignedTime";
import { claimableMinutes } from "../lib/autoAttach";
import { useObservationsShadow } from "../hooks/useObservationsShadow";
import { useImputationRules } from "../hooks/useImputationRules";
import { useMyCorrectionRequests } from "../hooks/useCorrectionRequests";
import { needsWorkerAction } from "../lib/anomalyState";
import {
  useWorkerTimesheets,
  useSubmitTimesheet,
  useLockTimesheet,
  useInitTimesheet,
  useSubmitAndLockTimesheet,
} from "../hooks/useTimesheet";
import { useManagerTeam } from "../hooks/useManagerTeam";
import { useTenantSettings } from "../hooks/useTenantSettings";
import { useUiStore } from "../state/uiStore";
import { Icons } from "../components/Icons";
import { ScreenHeader } from "../components/ScreenHeader";
import { Tabs } from "../components/Tabs";
import { TimelineWeekBoard } from "../components/TimelineWeekBoard";
import { currentPeriod, pendingPeriods } from "../lib/weekBoard";
import type { PeriodRange } from "../lib/weekBoard";
import { TimeBlockList } from "../components/TimeBlockList";
import { Sheet } from "../components/Sheet";
import { UnassignedSources } from "../components/UnassignedSources";
import { WeekReviewSection } from "../components/WeekReviewSection";
import type { ObservedState } from "../components/WeekReviewSection";
import { ApiError } from "../api/client";
import { TimelineEditorSheet } from "./TimelineEditorSheet";
import { useActivityCatalog } from "../hooks/useActivityCatalog";
import { getWorkerUrl } from "../lib/session";
import {
  formatDuration,
  formatMinutes,
  formatPeriodRange,
} from "../lib/format";
import { LOCK_ACTION, LOCK_NOTE } from "../lib/review";
import { TIMESHEET_STATUS_HINT, TIMESHEET_STATUS_TAG } from "../lib/statusMeta";
import { parseDateKey } from "../lib/date";
import { resolveRepoProjects } from "../lib/chantiers";
import {
  attachTargets,
  gitOnlyBranches,
  shadowSpanFits,
  weekOverlaps,
} from "../lib/weekReview";
import type {
  Timesheet,
  TimeDayBlock,
  TimesheetLine,
  TimesheetPeriodType,
} from "../api/types";

const DAY_NAMES_FR = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const MONTH_FR = [
  "janv.",
  "févr.",
  "mars",
  "avr.",
  "mai",
  "juin",
  "juil.",
  "août",
  "sept.",
  "oct.",
  "nov.",
  "déc.",
];

function formatDayLabel(key: string): string {
  const d = parseDateKey(key);
  return `${DAY_NAMES_FR[d.getDay()]} ${d.getDate()} ${MONTH_FR[d.getMonth()] ?? ""}`;
}

function formatDayMonth(key: string): string {
  const d = parseDateKey(key.slice(0, 10));
  return `${d.getDate()} ${MONTH_FR[d.getMonth()] ?? ""}`;
}

function localDateKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function minutesToHM(min: number): string {
  return formatDuration(min * 60000).hm;
}

/** Minutes de la journée sur des créneaux sans projet. */
function unassignedBlockMinutes(blocks: TimeDayBlock[]): number {
  return blocks
    .filter((b) => !b.project_id)
    .reduce((sum, b) => sum + Math.max(0, b.end_minute - b.start_minute), 0);
}

/** Une période navigable : une feuille consolidée, ou une période qui l'attend. */
interface PeriodSlot extends PeriodRange {
  ts?: Timesheet;
}

type PeriodView = "projects" | "detail";

type SubmitKind = "submit" | "submitAndLock";

function submitErrorSubtitle(e: unknown): string {
  return e instanceof ApiError && e.code === "imputation_invalid"
    ? "Des activités imputables n'ont pas de projet : imputez-les, puis soumettez."
    : "Vérifiez qu'aucune activité ne se chevauche, puis réessayez.";
}

export function TimesheetScreen() {
  // `?jour=YYYY-MM-DD` (liens de l'accueil) : la période de ce jour, dépliée.
  const [params] = useSearchParams();
  const jourParam = params.get("jour");
  const jour =
    jourParam && /^\d{4}-\d{2}-\d{2}$/.test(jourParam) ? jourParam : null;
  // Début de la période choisie. null : pas encore de choix, on se place sur
  // le jour demandé, à défaut le brouillon à soumettre, à défaut la période en
  // cours. Repérée par sa date et non par son rang : la liste se réordonne
  // quand une feuille est créée.
  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  const [expandedDay, setExpandedDay] = useState<string | null>(jour);
  const [periodView, setPeriodView] = useState<PeriodView>("detail");
  const [editorDayKey, setEditorDayKey] = useState<string | null>(null);
  const [confirmKind, setConfirmKind] = useState<SubmitKind | null>(null);
  // Liste du temps sans projet ouverte depuis l'avertissement ; chaque demande
  // incrémente le compteur, qui y ramène le focus.
  const [unassignedFocus, setUnassignedFocus] = useState(0);
  const unassignedRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const role = useRole();
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);

  const workerUrl: string = user
    ? (() => {
        try {
          return getWorkerUrl(user);
        } catch {
          return "";
        }
      })()
    : "";

  const { data: settings } = useTenantSettings();
  const periodType: TimesheetPeriodType =
    settings?.timesheet_period_type ?? "weekly";

  const { data: timesheetsData } = useWorkerTimesheets(workerUrl);
  const { data: anomaliesData } = useMyCorrectionRequests(!!workerUrl);
  const submitMutation = useSubmitTimesheet();
  const lockMutation = useLockTimesheet();
  const initMutation = useInitTimesheet();
  const submitAndLock = useSubmitAndLockTimesheet();
  // Valider sa propre feuille : l'admin toujours ; le manager seulement s'il
  // fait partie de sa propre équipe (TAWA, assertCanManageWorker).
  const team = useManagerTeam(role === "manager");
  const canValidateOwn =
    role === "admin" ||
    (role === "manager" &&
      !!team.data?.workers.some((w) => w.worker_url === workerUrl));
  const catalog = useActivityCatalog();

  const timesheets = useMemo(
    () => timesheetsData?.timesheets ?? [],
    [timesheetsData],
  );
  // De la plus récente à la plus ancienne : les périodes qui attendent leur
  // consolidation, puis les feuilles (triées par period_start décroissant).
  const pending = useMemo(() => {
    const latest = timesheets[0];
    return pendingPeriods(
      periodType,
      latest && {
        start: latest.period_start.slice(0, 10),
        end: latest.period_end.slice(0, 10),
      },
    );
  }, [periodType, timesheets]);
  const slots = useMemo<PeriodSlot[]>(() => {
    const list: PeriodSlot[] = [
      ...pending,
      ...timesheets.map((t) => ({
        start: t.period_start.slice(0, 10),
        end: t.period_end.slice(0, 10),
        ts: t,
      })),
    ];
    // Un jour demandé hors de toute période listée (avant la dernière feuille,
    // sans feuille lui-même) reçoit la sienne, à son rang.
    if (jour && !list.some((p) => p.start <= jour && jour <= p.end)) {
      list.push(currentPeriod(periodType, parseDateKey(jour)));
      list.sort((a, b) => b.start.localeCompare(a.start));
    }
    return list;
  }, [pending, timesheets, jour, periodType]);
  // Ouverture sur le brouillon à soumettre, le plus récent (les feuilles sont
  // triées par period_start décroissant) : l'ouvrir ailleurs le laissait passer
  // inaperçu. Sans brouillon, sur la période en cours, qui attend sa
  // consolidation (les périodes en attente précèdent les feuilles dans `slots`).
  const todayKey = localDateKey(new Date());
  const draftIdx = slots.findIndex((s) => s.ts?.status === "draft");
  const draft = draftIdx >= 0 ? slots[draftIdx] : undefined;
  const currentIdx = slots.findIndex(
    (s) => s.start <= todayKey && todayKey <= s.end,
  );
  const jourIdx = jour
    ? slots.findIndex((s) => s.start <= jour && jour <= s.end)
    : -1;
  const chosenIdx =
    selectedStart !== null
      ? slots.findIndex((s) => s.start === selectedStart)
      : -1;
  const slotIdx = Math.min(
    chosenIdx >= 0
      ? chosenIdx
      : jourIdx >= 0
        ? jourIdx
        : draft
          ? draftIdx
          : Math.max(0, currentIdx),
    slots.length - 1,
  );
  const selectIdx = (i: number) => {
    const target = slots[i];
    if (target) setSelectedStart(target.start);
  };
  const slot: PeriodSlot | undefined = slots[slotIdx];
  const ts: Timesheet | undefined = slot?.ts;
  const myAnomalies = anomaliesData?.requests ?? [];
  const anomalyCount = myAnomalies.filter((a) =>
    needsWorkerAction(a, myAnomalies),
  ).length;

  // Période affichée : celle sélectionnée, feuille ou non, sinon la période en
  // cours. Une feuille est une consolidation, déclenchée par un admin ou le cron
  // hebdomadaire : pas une condition d'existence des pointages. S'y adosser
  // laissait un worker sans feuille générée devant un écran vide alors que ses
  // time_events étaient là.
  const period = useMemo(
    () =>
      slot ? { start: slot.start, end: slot.end } : currentPeriod(periodType),
    [slot, periodType],
  );

  // Temps de la période, calculé par le serveur : la source unique de Détail et
  // de Semaine (comme des compteurs du profil et de la vue manager).
  const { daysByKey } = useWorkerTimeDays(workerUrl, period.start, period.end, {
    enabled: !!workerUrl,
  });

  // Revue avant soumission : brouillon, ou période qui attend sa feuille.
  const reviewing = !!workerUrl && (!ts || ts.status === "draft");

  // Temps sans projet de la période relue : imputé sur place, averti avant
  // l'envoi.
  const unassigned = useUnassignedTime(workerUrl, period.start, period.end, {
    enabled: reviewing,
  });
  useAutoAttach(
    unassigned.data?.activities,
    [...daysByKey.values()],
    reviewing,
    "dans la frise de Ma journée",
  );
  // Moins de cinq minutes sans projet sur un jour : plus réclamé. La
  // soumission, elle, compte tout ce que TAWA refuserait.
  const unassignedMin = reviewing
    ? claimableMinutes(unassigned.data?.activities ?? [])
    : 0;
  const totalUnassignedMin = reviewing
    ? (unassigned.data?.total_worked_minutes ?? 0)
    : 0;
  // Pas de soumission tant que le compte n'est pas connu : l'avertissement
  // serait sauté. Une erreur de lecture laisse soumettre sans avertir.
  const unassignedPending = ts?.status === "draft" && unassigned.isLoading;
  const blockingMin = (unassigned.data?.activities ?? [])
    .filter((a) => a.requires_project)
    .reduce((sum, a) => sum + a.worked_minutes, 0);

  // Événements bruts du seul jour ouvert : édition self-service des pointages.
  // Rien à charger pour les autres jours, les durées viennent du serveur.
  const { eventsByDay } = useWorkerTimelineDays(
    workerUrl,
    expandedDay ?? "",
    expandedDay ?? "",
    { enabled: !!workerUrl && !!expandedDay },
  );

  // Lignes groupées par jour. Un brouillon (ou une période sans feuille) suit les
  // pointages en direct ; dès la soumission, la feuille garde son snapshot figé.
  const dayGroups = useMemo(() => {
    const byDay = new Map<string, TimesheetLine[]>();
    if (!ts || ts.status === "draft") {
      for (const d of daysByKey.values()) {
        const lines = d.lines.filter(
          (l) => l.worked_minutes > 0 || l.break_minutes > 0,
        );
        if (lines.length > 0) byDay.set(d.date, lines);
      }
    } else {
      for (const l of ts.lines ?? []) {
        const arr = byDay.get(l.work_date) ?? [];
        arr.push(l);
        byDay.set(l.work_date, arr);
      }
    }
    return [...byDay.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [ts, daysByKey]);

  // Libellés du catalogue indexés par code : la board affiche « Développement »
  // là où l'event ne porte que « dev ».
  // Dépendance sur `catalog.data?.activities` et non sur `fullCatalog` : ce
  // dernier est un `?? []` recréé à chaque render, qui relancerait le memo à
  // chaque fois : et avec lui le rendu de la board.
  const activityLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of catalog.data?.activities ?? []) m.set(a.code, a.label);
    return m;
  }, [catalog.data?.activities]);

  const shadowFits = shadowSpanFits(period.start, period.end);
  const shadow = useObservationsShadow(
    workerUrl,
    period.start,
    period.end,
    reviewing && shadowFits,
  );
  const rules = useImputationRules(reviewing);
  const overlaps = useMemo(
    () => weekOverlaps(daysByKey.values(), period.start, period.end),
    [daysByKey, period],
  );
  const shadowDays = shadow.data?.days;
  const gitOnly = useMemo(
    () => (shadowDays ? gitOnlyBranches(shadowDays) : null),
    [shadowDays],
  );
  const attach = useMemo(
    () =>
      shadowDays && rules.data
        ? attachTargets(
            shadowDays,
            resolveRepoProjects(rules.data.rules, [], workerUrl),
            workerUrl,
          )
        : [],
    [shadowDays, rules.data, workerUrl],
  );
  const shadowState: ObservedState = !shadowFits
    ? "too-long"
    : shadow.isError
      ? "error"
      : shadow.data
        ? "ready"
        : "loading";
  const attachState: ObservedState =
    shadowState === "ready"
      ? rules.isError
        ? "error"
        : rules.data
          ? "ready"
          : "loading"
      : shadowState;

  const daysWorked = dayGroups.filter((g) =>
    g[1].some((l) => l.worked_minutes > 0),
  ).length;
  // Même source que la liste des jours : le direct tant que la feuille est en
  // brouillon ou absente, le snapshot une fois soumise.
  const totalWorked = dayGroups.reduce(
    (sum, [, lines]) => sum + lines.reduce((s, l) => s + l.worked_minutes, 0),
    0,
  );

  const busy =
    submitMutation.isPending ||
    lockMutation.isPending ||
    submitAndLock.isPending ||
    initMutation.isPending;
  const canSubmit = !busy && !unassignedPending && ts?.status === "draft";
  // TAWA ne verrouille qu'une feuille soumise (409 sur une feuille corrigée).
  const canLock = canValidateOwn && ts?.status === "submitted";
  // Édition self-service : le worker modifie sa feuille tant qu'elle est en
  // brouillon (révision du lundi). Verrouillée après soumission.
  const canSelfEdit = ts?.status === "draft";
  // Réimputer : toute feuille couvrant le jour en brouillon, ou aucune encore.
  const canReimpute = !ts || ts.status === "draft";

  async function handleCreate() {
    if (!slot || !workerUrl) return;
    setSelectedStart(slot.start);
    try {
      await initMutation.mutateAsync({
        workerUrl,
        start: slot.start,
        end: slot.end,
      });
      pushToast({ variant: "info", title: "Feuille de temps créée" });
    } catch {
      pushToast({
        variant: "error",
        title: "Feuille non créée",
        subtitle: "TAWA a refusé la création. Réessayez dans un instant.",
      });
    }
  }

  async function handleSubmitAndLock() {
    if (!ts) return;
    setSelectedStart(slot?.start ?? null);
    try {
      const { locked } = await submitAndLock.mutateAsync(ts.id);
      if (locked) {
        pushToast({
          variant: "info",
          title: "Feuille soumise et validée",
          subtitle: "Elle part dans l'export paie.",
        });
      } else {
        pushToast({
          variant: "error",
          title: "Feuille soumise, mais pas validée",
          subtitle:
            "La validation a échoué. La feuille reste soumise : réessayez « Valider et verrouiller ».",
        });
      }
    } catch (e) {
      pushToast({
        variant: "error",
        title: "Feuille non soumise",
        subtitle: submitErrorSubtitle(e),
      });
    }
  }

  async function handleSubmit() {
    if (!ts) return;
    // Soumise, la feuille n'est plus le brouillon d'ouverture : sans choix figé,
    // la vue sauterait sur la période en cours.
    setSelectedStart(slot?.start ?? null);
    try {
      await submitMutation.mutateAsync(ts.id);
      pushToast({ variant: "info", title: "Feuille de temps soumise" });
    } catch (e) {
      pushToast({
        variant: "error",
        title: "Feuille non soumise",
        subtitle: submitErrorSubtitle(e),
      });
    }
  }

  function requestSubmit(kind: SubmitKind) {
    if (unassignedMin > 0 || blockingMin > 0) {
      setConfirmKind(kind);
      return;
    }
    void (kind === "submit" ? handleSubmit() : handleSubmitAndLock());
  }

  function confirmSubmit() {
    const kind = confirmKind;
    setConfirmKind(null);
    if (kind) void (kind === "submit" ? handleSubmit() : handleSubmitAndLock());
  }

  function showUnassignedList() {
    setUnassignedFocus((n) => n + 1);
  }

  function imputeFromConfirm() {
    setConfirmKind(null);
    showUnassignedList();
  }

  // Après la fermeture de l'avertissement, qui rend le focus au bouton.
  useEffect(() => {
    if (unassignedFocus === 0) return;
    const el = unassignedRef.current;
    el?.scrollIntoView?.({ block: "start", behavior: "smooth" });
    el?.focus();
  }, [unassignedFocus]);

  async function handleLock() {
    if (!ts) return;
    try {
      await lockMutation.mutateAsync(ts.id);
      pushToast({
        variant: "info",
        title: "Feuille validée et verrouillée",
        subtitle: "Elle part dans l'export paie.",
      });
    } catch {
      pushToast({
        variant: "error",
        title: "Feuille non validée",
        subtitle: "La feuille reste soumise. Réessayez dans un instant.",
      });
    }
  }

  // Un seul endroit dit où en est la feuille : sous la période.
  const periodStatus = !ts
    ? "Pas encore créée"
    : ts.status === "draft"
      ? "À soumettre"
      : TIMESHEET_STATUS_TAG[ts.status].label;
  const submitHint = !ts
    ? "Pas encore de feuille pour cette période : créez-la pour la soumettre"
    : ts.status === "submitted" && canLock
      ? LOCK_NOTE
      : TIMESHEET_STATUS_HINT[ts.status];

  return (
    <>
      <ScreenHeader
        title="Feuille de temps"
        aside={periodType === "monthly" ? "Mensuelle" : "Hebdomadaire"}
      />

      {draft && ts?.status !== "draft" && (
        <div className="submit-bar">
          <div className="text">
            <div className="ttl">
              Feuille du {formatDayMonth(draft.start)} au{" "}
              {formatDayMonth(draft.end)} à soumettre
            </div>
          </div>
          <button className="submit-btn" onClick={() => selectIdx(draftIdx)}>
            Ouvrir la feuille
          </button>
        </div>
      )}

      <div className="period-nav">
        <button
          className="arrow"
          aria-label="Période précédente"
          onClick={() => selectIdx(Math.min(slots.length - 1, slotIdx + 1))}
          disabled={slotIdx >= slots.length - 1}
          style={{ opacity: slotIdx >= slots.length - 1 ? 0.4 : 1 }}
        >
          <Icons.chevL size={16} />
        </button>
        <div className="label">
          <div className="wk">{periodStatus}</div>
          <div className="rng">
            {slot ? formatPeriodRange(slot.start, slot.end) : ""}
          </div>
        </div>
        <button
          className="arrow"
          aria-label="Période suivante"
          onClick={() => selectIdx(Math.max(0, slotIdx - 1))}
          disabled={slotIdx <= 0}
          style={{ opacity: slotIdx <= 0 ? 0.4 : 1 }}
        >
          <Icons.chev size={16} />
        </button>
      </div>

      <div className="summary">
        <div className="item">
          <div className="k">Heures</div>
          <div className="v">{minutesToHM(totalWorked)}</div>
        </div>
        <div className="item">
          <div className="k">Jours travaillés</div>
          <div className="v">{daysWorked}</div>
        </div>
        {reviewing && unassigned.data && (
          <div className="item">
            <div className="k">Sans projet</div>
            <div className={`v${unassignedMin > 0 ? " warn" : ""}`}>
              {minutesToHM(unassignedMin)}
            </div>
          </div>
        )}
        {ts?.export_state === "exported" && (
          <div className="item">
            <div className="k">Paie</div>
            <div className="v" style={{ fontSize: "var(--fs-md)" }}>
              Exportée
            </div>
          </div>
        )}
      </div>

      {anomalyCount > 0 && (
        <div className="anom-banner">
          <div className="ic">
            <Icons.warn size={18} sw={2} />
          </div>
          <div className="text">
            <div className="ttl">
              {anomalyCount} anomalie{anomalyCount > 1 ? "s" : ""} à justifier
            </div>
            <div className="sub">
              Des pointages ont été signalés par la détection hebdomadaire
            </div>
            <button className="cta" onClick={() => navigate("/anomalies")}>
              Voir les anomalies
            </button>
          </div>
        </div>
      )}

      {reviewing && (
        <WeekReviewSection
          overlaps={overlaps}
          unassignedMin={unassigned.data ? unassignedMin : null}
          gitOnly={gitOnly}
          gitOnlyState={shadowState}
          attach={attach}
          attachState={attachState}
          labels={activityLabels}
          dayLabel={formatDayLabel}
        >
          {unassignedMin > 0 && (
            <div
              ref={unassignedRef}
              tabIndex={-1}
              className="unassigned-anchor"
            >
              <UnassignedSources
                workerUrl={workerUrl}
                from={period.start}
                to={period.end}
                editable={canReimpute}
              />
            </div>
          )}
        </WeekReviewSection>
      )}

      <Tabs
        className="seg-block"
        label="Vue de la période"
        value={periodView}
        onChange={setPeriodView}
        items={[
          { id: "detail", label: "Par jour" },
          { id: "projects", label: "Par projet" },
        ]}
      />

      {periodView === "projects" && (
        <TimelineWeekBoard
          periodStart={period.start}
          periodEnd={period.end}
          daysByKey={daysByKey}
          labels={activityLabels}
        />
      )}

      {periodView === "detail" && (
        <div className="day-list">
          {dayGroups.length === 0 && (
            <div className="empty-state">
              <div className="sub">
                Aucune activité enregistrée sur cette période.
              </div>
            </div>
          )}
          {dayGroups.map(([dayKey, lines]) => {
            const dayWorked = lines.reduce((s, l) => s + l.worked_minutes, 0);
            const dayBreak = lines.reduce((s, l) => s + l.break_minutes, 0);
            const blocks = daysByKey.get(dayKey)?.blocks ?? [];
            const dayUnassigned = reviewing
              ? unassignedBlockMinutes(blocks)
              : 0;
            const expanded = expandedDay === dayKey;
            const dayEventCount = (eventsByDay.get(dayKey) ?? []).length;
            const toggle = () => setExpandedDay(expanded ? null : dayKey);

            return (
              <div key={dayKey}>
                <div
                  className="day-row"
                  onClick={toggle}
                  style={{ cursor: "pointer" }}
                  role="button"
                  aria-expanded={expanded}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggle();
                    }
                  }}
                >
                  <div className="info">
                    <div className="ttl">{formatDayLabel(dayKey)}</div>
                    <div className="sub">
                      {minutesToHM(dayWorked)}
                      {dayBreak > 0 && (
                        <span style={{ color: "var(--fg3)" }}>
                          {" "}
                          • pause {minutesToHM(dayBreak)}
                        </span>
                      )}
                    </div>
                  </div>
                  {dayUnassigned > 0 ? (
                    <div className="status warn">
                      {minutesToHM(dayUnassigned)} sans projet
                    </div>
                  ) : (
                    <Icons.chev size={14} />
                  )}
                </div>

                {expanded && (
                  <div className="detail-panel">
                    {blocks.length > 0 ? (
                      <TimeBlockList
                        blocks={blocks}
                        editable={canReimpute}
                        labels={activityLabels}
                      />
                    ) : (
                      <div className="sub">Aucun créneau ce jour.</div>
                    )}
                    {canSelfEdit && (
                      <div style={{ marginTop: "var(--sp-3)" }}>
                        <button
                          className="pill-btn secondary"
                          onClick={() => setEditorDayKey(dayKey)}
                        >
                          Corriger les pointages
                          {dayEventCount > 0 ? ` (${dayEventCount})` : ""}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Brouillon ou période sans feuille : la barre reste collée au bas de la
          zone qui défile, l'action visible sans descendre sous le détail. */}
      <div
        className={`submit-bar${!ts || ts.status === "draft" ? " sticky" : ""}`}
      >
        <div className="text">
          <div className="sub">{submitHint}</div>
        </div>
        <div className="submit-actions">
          {!ts && (
            <button
              className="submit-btn"
              disabled={busy || !workerUrl}
              onClick={() => void handleCreate()}
            >
              {initMutation.isPending ? "Création…" : "Créer ma feuille"}
            </button>
          )}
          {canLock && (
            <button
              className="submit-btn"
              disabled={busy}
              onClick={() => void handleLock()}
            >
              {lockMutation.isPending ? "Validation…" : LOCK_ACTION}
            </button>
          )}
          {ts?.status === "draft" && (
            <button
              className={canValidateOwn ? "submit-btn secondary" : "submit-btn"}
              disabled={!canSubmit}
              onClick={() => requestSubmit("submit")}
            >
              {submitMutation.isPending ? "Envoi…" : "Soumettre"}
            </button>
          )}
          {ts?.status === "draft" && canValidateOwn && (
            <button
              className="submit-btn"
              disabled={!canSubmit}
              onClick={() => requestSubmit("submitAndLock")}
            >
              {submitAndLock.isPending ? "Validation…" : "Soumettre et valider"}
            </button>
          )}
        </div>
      </div>

      {confirmKind && (
        <Sheet
          title="Du temps reste sans projet"
          onClose={() => setConfirmKind(null)}
          footer={
            <div className="sheet-cta wrap">
              <button
                type="button"
                className="pill-btn secondary tap"
                onClick={imputeFromConfirm}
              >
                Imputer maintenant
              </button>
              <button type="button" className="primary" onClick={confirmSubmit}>
                {confirmKind === "submit"
                  ? "Soumettre quand même"
                  : "Soumettre et valider quand même"}
              </button>
            </div>
          }
        >
          <p className="submit-warning">
            {formatMinutes(totalUnassignedMin)} sans projet sur la période. Ce
            temps ne sera imputé à aucun projet.
          </p>
          {blockingMin > 0 && (
            <p className="submit-warning blocking">
              Dont {formatMinutes(blockingMin)} sur des activités imputables :
              la soumission sera refusée tant qu'elles restent sans projet.
            </p>
          )}
        </Sheet>
      )}

      {editorDayKey && (
        <TimelineEditorSheet
          dayKey={editorDayKey}
          dayLabel={formatDayLabel(editorDayKey)}
          events={eventsByDay.get(editorDayKey) ?? []}
          editable={canSelfEdit}
          onClose={() => setEditorDayKey(null)}
        />
      )}
    </>
  );
}
